import type { FC } from 'react'

/** Reusable table skeleton rows. Matches Bootstrap placeholder classes. */
export const TableSkeleton: FC<{ rows?: number; cols: number }> = ({ rows = 8, cols }) => (
  <>
    {Array.from({ length: rows }).map((_, i) => (
      <tr key={`sk-${i}`}>
        {Array.from({ length: cols }).map((__, c) => (
          <td key={`sk-${i}-${c}`} className="py-3">
            <div className="placeholder-wave">
              <div className="placeholder w-100" style={{ height: 14, borderRadius: 4 }} />
            </div>
          </td>
        ))}
      </tr>
    ))}
  </>
)

/** Simple full-page spinner overlay for initial data loads. */
export const PageSpinner: FC<{ message?: string }> = ({ message = 'Loading…' }) => (
  <div className="d-flex flex-column align-items-center justify-content-center py-8">
    <div className="spinner-border text-primary mb-3" role="status" style={{ width: '2.5rem', height: '2.5rem' }}>
      <span className="visually-hidden">{message}</span>
    </div>
    <span className="text-muted fs-7">{message}</span>
  </div>
)

/** Shimmer card placeholder for dashboard KPI cards. */
export const KpiCardSkeleton: FC = () => (
  <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm">
    <div className="placeholder-wave mb-2">
      <div className="placeholder rounded" style={{ width: '60%', height: 10 }} />
    </div>
    <div className="placeholder-wave mb-1">
      <div className="placeholder rounded" style={{ width: '40%', height: 24 }} />
    </div>
    <div className="placeholder-wave mb-2">
      <div className="placeholder rounded" style={{ width: '80%', height: 10 }} />
    </div>
    <div className="placeholder-wave">
      <div className="placeholder rounded w-100" style={{ height: 40 }} />
    </div>
  </div>
)

/** Shimmer card placeholder for generic content cards. */
export const CardSkeleton: FC<{ lines?: number }> = ({ lines = 3 }) => (
  <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm">
    <div className="placeholder-wave mb-3">
      <div className="placeholder rounded" style={{ width: '35%', height: 12 }} />
    </div>
    {Array.from({ length: lines }).map((_, i) => (
      <div key={i} className="placeholder-wave mb-2">
        <div className="placeholder rounded w-100" style={{ height: 10 }} />
      </div>
    ))}
  </div>
)

/** Shimmer list of action/report buttons (e.g. for modal selections). */
export const ListButtonSkeleton: FC<{ count?: number }> = ({ count = 5 }) => (
  <div className="d-flex flex-column gap-4 py-2">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="mb-2" style={{ paddingRight: '4rem' }}>
        <div className="placeholder-wave mb-2">
          <div className="placeholder rounded" style={{ width: `${40 + (i % 3) * 15}%`, height: 14 }} />
        </div>
        <div className="placeholder-wave">
          <div
            className="placeholder rounded w-100"
            style={{ height: 42, borderRadius: 8, opacity: 0.8 }}
          />
        </div>
      </div>
    ))}
  </div>
)

/** Shimmer tree/sidebar accordion items. */
export const SidebarTreeSkeleton: FC<{ count?: number }> = ({ count = 4 }) => (
  <div className="d-flex flex-column gap-3 py-2">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="border rounded p-3 bg-white">
        <div className="placeholder-wave mb-2">
          <div className="placeholder rounded" style={{ width: `${50 + (i % 2) * 20}%`, height: 16 }} />
        </div>
        <div className="d-flex flex-column gap-2 ps-3 pt-2">
          <div className="placeholder-wave">
            <div className="placeholder rounded" style={{ width: '70%', height: 12 }} />
          </div>
          <div className="placeholder-wave">
            <div className="placeholder rounded" style={{ width: '55%', height: 12 }} />
          </div>
        </div>
      </div>
    ))}
  </div>
)

/** Shimmer dashboard grid (KPI cards + 2 large tables/cards + 8 widget cards). */
export const DashboardGridSkeleton: FC = () => (
  <div className="d-flex flex-column gap-5 md:gap-6">
    {/* ROW 1: 6 KPI CARDS */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-5 md:gap-6">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={`kpi-${i}`} className="md:col-span-1 lg:col-span-2">
          <KpiCardSkeleton />
        </div>
      ))}
    </div>

    {/* ROW 2: VESSEL STATUS (8 cols) + VOYAGE TIMELINE (4 cols) */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-5 md:gap-6">
      <div className="md:col-span-1 lg:col-span-8 bg-white rounded-2xl p-5 border border-slate-200 shadow-sm">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="placeholder-wave" style={{ width: '28%' }}>
            <div className="placeholder rounded w-100" style={{ height: 18 }} />
          </div>
          <div className="d-flex gap-2" style={{ width: '30%' }}>
            <div className="placeholder-wave flex-grow-1">
              <div className="placeholder rounded w-100" style={{ height: 28 }} />
            </div>
            <div className="placeholder-wave flex-grow-1">
              <div className="placeholder rounded w-100" style={{ height: 28 }} />
            </div>
          </div>
        </div>
        <div className="table-responsive">
          <table className="table table-bordered align-middle mb-0">
            <tbody>
              <TableSkeleton rows={6} cols={5} />
            </tbody>
          </table>
        </div>
      </div>
      <div className="md:col-span-1 lg:col-span-4 bg-white rounded-2xl p-5 border border-slate-200 shadow-sm">
        <div className="placeholder-wave mb-4">
          <div className="placeholder rounded" style={{ width: '45%', height: 18 }} />
        </div>
        <div className="d-flex flex-column gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="p-3 rounded-xl border border-slate-100 bg-slate-50">
              <div className="placeholder-wave mb-2">
                <div className="placeholder rounded" style={{ width: '60%', height: 14 }} />
              </div>
              <div className="placeholder-wave mb-2">
                <div className="placeholder rounded" style={{ width: '40%', height: 10 }} />
              </div>
              <div className="placeholder-wave">
                <div className="placeholder rounded w-100" style={{ height: 6, borderRadius: 3 }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* ROW 3: WORK QUEUE + CARGO + DPR + CII (4 cards, 3 cols each) */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-5 md:gap-6">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={`row3-${i}`} className="md:col-span-1 lg:col-span-3 bg-white rounded-2xl p-5 border border-slate-200 shadow-sm">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div className="placeholder-wave" style={{ width: '50%' }}>
              <div className="placeholder rounded w-100" style={{ height: 16 }} />
            </div>
            <div className="placeholder-wave" style={{ width: '20%' }}>
              <div className="placeholder rounded w-100" style={{ height: 12 }} />
            </div>
          </div>
          <div className="d-flex flex-column gap-3">
            {Array.from({ length: 3 }).map((__, j) => (
              <div key={j} className="d-flex align-items-center justify-content-between p-2 rounded-lg bg-slate-50">
                <div className="placeholder-wave" style={{ width: '55%' }}>
                  <div className="placeholder rounded w-100" style={{ height: 12 }} />
                </div>
                <div className="placeholder-wave" style={{ width: '25%' }}>
                  <div className="placeholder rounded w-100" style={{ height: 16, borderRadius: 6 }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>

    {/* ROW 4: DEFECTS + CONSUMPTION + EVENTS + INSIGHTS (4 cards, 3 cols each) */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-5 md:gap-6">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={`row4-${i}`} className="md:col-span-1 lg:col-span-3 bg-white rounded-2xl p-5 border border-slate-200 shadow-sm">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div className="placeholder-wave" style={{ width: '45%' }}>
              <div className="placeholder rounded w-100" style={{ height: 16 }} />
            </div>
            <div className="placeholder-wave" style={{ width: '22%' }}>
              <div className="placeholder rounded w-100" style={{ height: 12 }} />
            </div>
          </div>
          <div className="d-flex flex-column gap-2">
            <div className="placeholder-wave mb-1">
              <div className="placeholder rounded" style={{ width: '35%', height: 22 }} />
            </div>
            <div className="placeholder-wave mb-2">
              <div className="placeholder rounded" style={{ width: '70%', height: 10 }} />
            </div>
            <div className="placeholder-wave">
              <div className="placeholder rounded w-100" style={{ height: 24, borderRadius: 6 }} />
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
)

/** Calendar planning matrix skeleton: mimics sticky vessel columns and 12-month calendar event cards */
export const PlanningCalendarSkeleton: FC<{ rows?: number }> = ({ rows = 8 }) => {
  const months = Array.from({ length: 12 })
  return (
    <>
      {Array.from({ length: rows }).map((_, r) => (
        <tr key={`cal-sk-${r}`} style={{ borderBottom: '1px solid #eff2f5' }}>
          {/* SR/NO column */}
          <td
            className="text-center"
            style={{
              position: 'sticky',
              left: 0,
              backgroundColor: r % 2 === 0 ? '#fff' : '#fafbff',
              zIndex: 2,
              padding: '10px 4px',
              width: '50px',
            }}
          >
            <div className="placeholder-wave">
              <div className="placeholder mx-auto" style={{ width: 18, height: 14, borderRadius: 3 }} />
            </div>
          </td>

          {/* Vessel Name column */}
          <td
            style={{
              position: 'sticky',
              left: 50,
              backgroundColor: r % 2 === 0 ? '#fff' : '#fafbff',
              zIndex: 1,
              padding: '10px 12px',
              width: '160px',
            }}
          >
            <div className="placeholder-wave">
              <div
                className="placeholder"
                style={{ width: `${75 + (r % 3) * 25}px`, height: 14, borderRadius: 4 }}
              />
            </div>
          </td>

          {/* 12 Month calendar cells */}
          {months.map((__, m) => {
            const hasEvent = (r * 3 + m) % 4 === 0 || (r + m * 2) % 6 === 1
            const hasSecondEvent = hasEvent && (r + m) % 5 === 0

            return (
              <td
                key={`cal-sk-${r}-${m}`}
                style={{
                  verticalAlign: 'top',
                  padding: '6px 5px',
                  backgroundColor: r % 2 === 0 ? '#fff' : '#fafbff',
                  minWidth: '75px',
                }}
              >
                {hasEvent ? (
                  <div className="d-flex flex-column gap-1">
                    <div
                      className="placeholder-wave"
                      style={{
                        padding: '4px 6px',
                        backgroundColor: '#f4f6fa',
                        borderLeft: '3px solid #b5b5c3',
                        borderRadius: '4px',
                        border: '1px solid #e4e6ef',
                        borderLeftWidth: '3px',
                      }}
                    >
                      <div
                        className="placeholder"
                        style={{
                          width: `${50 + ((r + m) % 4) * 12}%`,
                          height: 10,
                          borderRadius: 2,
                          backgroundColor: '#cdd3df',
                        }}
                      />
                    </div>
                    {hasSecondEvent && (
                      <div
                        className="placeholder-wave"
                        style={{
                          padding: '3px 6px',
                          backgroundColor: '#f4f6fa',
                          borderLeft: '3px solid #b5b5c3',
                          borderRadius: '4px',
                          border: '1px solid #e4e6ef',
                          borderLeftWidth: '3px',
                        }}
                      >
                        <div
                          className="placeholder"
                          style={{
                            width: '45%',
                            height: 10,
                            borderRadius: 2,
                            backgroundColor: '#cdd3df',
                          }}
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <div
                    style={{
                      height: '24px',
                      borderRadius: '4px',
                      border: '1px dashed #eff2f5',
                      backgroundColor: 'transparent',
                    }}
                  />
                )}
              </td>
            )
          })}
        </tr>
      ))}
    </>
  )
}

